// Designing forms: new fields the Reader adds, written as AcroForm widgets with appearance streams (ISO 32000-1 §12.7).

import { PName, PRef } from "./parse";
import { serialize } from "./writer";
import { lit, num, pdfText } from "./form";
import type { EventMap } from "./fieldactions";
import type { DataLink } from "./datalink";
import type { ImageFieldOpts, Pixels } from "./imagefield";

export type FieldType = "text" | "check" | "radio" | "combo" | "list" | "signature" | "image";
export type ValidationType = "none" | "number" | "integer" | "email" | "date" | "phone" | "regex" | "length";
/** How a field looks on the page: tinted fill (MK /BG), border colour (MK /BC), border width, underline-only, rounded corners. Colours are "#rrggbb". */
export interface FieldStyle { fill?: string; stroke?: string; borderWidth?: number; underline?: boolean; rounded?: boolean }
export interface FieldValidation { type: ValidationType; min?: number; max?: number; pattern?: string; message?: string }

/** A form field the Reader is adding. Coordinates are PDF user space. Radio buttons with the same `name` form one group, told apart by `exportValue`. */
export interface FieldAnnot {
    id: string;
    kind: "field";
    subject?: string;
    page: number;
    rect: [number, number, number, number];
    ftype: FieldType;
    name: string;
    exportValue?: string;
    /** The question this field answers; kept as the tooltip. */
    label?: string;
    required: boolean;
    readOnly: boolean;
    multiline: boolean;
    options: string[];
    maxLen: number;
    validation?: FieldValidation;
    defaultValue?: string;
    fontSize?: number;
    style?: FieldStyle;
    /** One box per character (text fields with a maximum length; Acrobat's "Comb of characters"). */
    comb?: boolean;
    /** Text field that hides what is typed. */
    password?: boolean;
    /** List box that allows several choices. */
    multiSelect?: boolean;
    /** Image field (ftype "image"): its settings and the picture put in so far. Written after the other fields, see applyExtras. */
    image?: { opts: ImageFieldOpts; picture?: Pixels & { source: string } };
    /** What the field does on its events, and where its value comes from and goes to. */
    events?: EventMap;
    link?: DataLink | null;
}

export const FF_PASSWORD = 8192;
export const FF_COMB = 1 << 24;
export const FF_MULTISELECT = 1 << 21;

/** "#rrggbb" as PDF colour components, or null. */
export function hexToRgb(hex: string | undefined): [number, number, number] | null {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex ?? "");
    return m ? [parseInt(m[1]!, 16) / 255, parseInt(m[2]!, 16) / 255, parseInt(m[3]!, 16) / 255] : null;
}

/** A style with its defaults (the blue tint the builder always used) filled in. */
export function resolveStyle(st: FieldStyle | undefined): { fill: [number, number, number]; stroke: [number, number, number]; width: number; underline: boolean; rounded: boolean } {
    return { fill: hexToRgb(st?.fill) ?? [0.93, 0.96, 1], stroke: hexToRgb(st?.stroke) ?? [0.3, 0.45, 0.8], width: st?.borderWidth ?? 0.8, underline: !!st?.underline, rounded: !!st?.rounded };
}

/** A field is a comb only when Acrobat would honour it: single-line, not a password, with a maximum length. */
export const isComb = (f: Pick<FieldAnnot, "ftype" | "comb" | "maxLen" | "multiline" | "password">): boolean => f.ftype === "text" && !!f.comb && f.maxLen > 0 && !f.multiline && !f.password;

/** Checks `value` against a validation rule; the message to show when it fails, or null. */
export function validateValue(v: FieldValidation | undefined, value: string): string | null {
    if (!v || v.type === "none" || value === "") return null;
    const bad = (d: string) => v.message || d;
    switch (v.type) {
        case "number": case "integer": {
            const n = Number(value);
            if (!Number.isFinite(n) || (v.type === "integer" && !Number.isInteger(n))) return bad(v.type === "integer" ? "Enter a whole number." : "Enter a number.");
            if (v.min !== undefined && n < v.min) return bad(`The number must be at least ${v.min}.`);
            if (v.max !== undefined && n > v.max) return bad(`The number must be at most ${v.max}.`);
            return null;
        }
        case "email": return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : bad("Enter an email address.");
        case "phone": return /^[+()\-.\s\d]{6,20}$/.test(value) ? null : bad("Enter a phone number.");
        case "date": return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)) ? null : bad("Enter a date as yyyy-mm-dd.");
        case "length":
            if (v.min !== undefined && value.length < v.min) return bad(`Enter at least ${v.min} characters.`);
            if (v.max !== undefined && value.length > v.max) return bad(`Enter at most ${v.max} characters.`);
            return null;
        case "regex":
            try { return new RegExp(v.pattern ?? "").test(value) ? null : bad("The answer is not in the expected format."); } catch { return null; }
    }
}

/** The same rule as Acrobat JavaScript, so other readers enforce it too. */
function validationJs(v: FieldValidation): string | null {
    const msg = JSON.stringify(v.message || "The answer is not valid.");
    const fail = `{app.alert(${msg});event.rc=false;}`;
    const pre = 'var v=event.value;if(v!==""&&';
    switch (v.type) {
        case "number": case "integer": return `${pre}(isNaN(v)${v.type === "integer" ? "||Number(v)%1!==0" : ""}${v.min !== undefined ? `||Number(v)<${v.min}` : ""}${v.max !== undefined ? `||Number(v)>${v.max}` : ""}))${fail}`;
        case "email": return `${pre}!/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(v))${fail}`;
        case "phone": return `${pre}!/^[+()\\-.\\s\\d]{6,20}$/.test(v))${fail}`;
        case "date": return `${pre}!/^\\d{4}-\\d{2}-\\d{2}$/.test(v))${fail}`;
        case "length": return `${pre}(${v.min !== undefined ? `v.length<${v.min}` : "false"}||${v.max !== undefined ? `v.length>${v.max}` : "false"}))${fail}`;
        case "regex": return v.pattern ? `${pre}!new RegExp(${JSON.stringify(v.pattern)}).test(v))${fail}` : null;
        default: return null;
    }
}

const ellipse = (cx: number, cy: number, rx: number, ry: number): string => {
    const k = 0.5523;
    return `${num(cx + rx)} ${num(cy)} m ${num(cx + rx)} ${num(cy + k * ry)} ${num(cx + k * rx)} ${num(cy + ry)} ${num(cx)} ${num(cy + ry)} c ${num(cx - k * rx)} ${num(cy + ry)} ${num(cx - rx)} ${num(cy + k * ry)} ${num(cx - rx)} ${num(cy)} c ${num(cx - rx)} ${num(cy - k * ry)} ${num(cx - k * rx)} ${num(cy - ry)} ${num(cx)} ${num(cy - ry)} c ${num(cx + k * rx)} ${num(cy - ry)} ${num(cx + rx)} ${num(cy - k * ry)} ${num(cx + rx)} ${num(cy)} c`;
};
const rgb = (c: number[]) => c.map((x) => num(x)).join(" ");
const roundedRect = (x: number, y: number, w: number, h: number, r: number): string => {
    const k = 0.5523 * r;
    return `${num(x + r)} ${num(y)} m ${num(x + w - r)} ${num(y)} l ${num(x + w - r + k)} ${num(y)} ${num(x + w)} ${num(y + r - k)} ${num(x + w)} ${num(y + r)} c ${num(x + w)} ${num(y + h - r)} l ${num(x + w)} ${num(y + h - r + k)} ${num(x + w - r + k)} ${num(y + h)} ${num(x + w - r)} ${num(y + h)} c ${num(x + r)} ${num(y + h)} l ${num(x + r - k)} ${num(y + h)} ${num(x)} ${num(y + h - r + k)} ${num(x)} ${num(y + h - r)} c ${num(x)} ${num(y + r)} l ${num(x)} ${num(y + r - k)} ${num(x + r - k)} ${num(y)} ${num(x + r)} ${num(y)} c h`;
};
/** Appearance of the empty field: tinted fill, border (or only an underline), comb dividers, and a small red asterisk when the answer is required. */
const box = (w: number, h: number, round: boolean, st?: FieldStyle, extra?: { comb?: number; required?: boolean }): string => {
    const r = resolveStyle(st);
    const head = `${rgb(r.fill)} rg ${rgb(r.stroke)} RG ${num(r.width)} w`;
    let out: string;
    if (round) out = `${head} ${ellipse(w / 2, h / 2, w / 2 - 0.4, h / 2 - 0.4)} B\n`;
    else if (r.underline) out = `${rgb(r.fill)} rg 0 0 ${num(w)} ${num(h)} re f ${rgb(r.stroke)} RG ${num(Math.max(r.width, 1))} w 0 ${num(Math.max(r.width, 1) / 2)} m ${num(w)} ${num(Math.max(r.width, 1) / 2)} l S\n`;
    else if (r.rounded) out = `${head} ${roundedRect(0.4, 0.4, w - 0.8, h - 0.8, Math.min(6, h / 2 - 0.4))} B\n`;
    else out = `${head} 0.4 0.4 ${num(w - 0.8)} ${num(h - 0.8)} re B\n`;
    if (extra?.comb && extra.comb > 1) {
        out += `${rgb(r.stroke)} RG ${num(Math.min(r.width, 0.8))} w `;
        for (let i = 1; i < extra.comb; i++) out += `${num((w * i) / extra.comb)} 0.4 m ${num((w * i) / extra.comb)} ${num(h - 0.4)} l S `;
        out += "\n";
    }
    if (extra?.required && !round) {
        const cx = w - 6, cy = h - 6, a = 2.6;
        out += `0.86 0.15 0.15 RG 0.9 w ${num(cx)} ${num(cy - a)} m ${num(cx)} ${num(cy + a)} l S ${num(cx - a * 0.87)} ${num(cy - a * 0.5)} m ${num(cx + a * 0.87)} ${num(cy + a * 0.5)} l S ${num(cx - a * 0.87)} ${num(cy + a * 0.5)} m ${num(cx + a * 0.87)} ${num(cy - a * 0.5)} l S\n`;
    }
    return out;
};

export interface FieldWriteApi {
    newRef(): number;
    writeObj(num: number, body: string): void;
    writeStream(num: number, dict: string, data: Uint8Array, compress?: boolean): Promise<void>;
}

/** Writes the widgets of `fields` (all on one page); returns the page's new annotation references and the fields that go into /AcroForm /Fields. */
export async function writeFieldWidgets(fields: FieldAnnot[], pageRef: PRef, api: FieldWriteApi): Promise<{ widgets: PRef[]; top: PRef[] }> {
    const enc = new TextEncoder();
    const widgets: PRef[] = [];
    const top: PRef[] = [];
    const form = (n: number, w: number, h: number, content: string) => api.writeStream(n, `/Type /XObject /Subtype /Form /BBox [0 0 ${num(w)} ${num(h)}] /Resources << >>`, enc.encode(content));
    const groups = new Map<string, { parent: number; kids: number[]; v: string; required: boolean }>();

    for (const f of fields) {
        const [x0, y0, x1, y1] = f.rect;
        const w = x1 - x0, h = y1 - y0;
        const n = api.newRef();
        const flags = (f.readOnly ? 1 : 0) | (f.required ? 2 : 0);
        const common = `/Type /Annot /Subtype /Widget /Rect [${[x0, y0, x1, y1].map(num).join(" ")}] /P ${pageRef.num} ${pageRef.gen} R /F 4 /NM ${lit(f.id)} ${f.label ? `/TU ${lit(pdfText(f.label))} ` : ""}/MK << /BC [${rgb(resolveStyle(f.style).stroke)}] /BG [${rgb(resolveStyle(f.style).fill)}] >> /BS << /W ${num(resolveStyle(f.style).width)} /S ${resolveStyle(f.style).underline ? "/U" : "/S"} >> `;
        const js = f.validation && f.validation.type !== "none" ? validationJs(f.validation) : null;
        const extra = f.validation && f.validation.type !== "none" ? `/FGValidate ${lit(JSON.stringify(f.validation))} ${js ? `/AA << /V << /S /JavaScript /JS ${lit(js)} >> >> ` : ""}` : "";
        if (f.ftype === "check" || f.ftype === "radio") {
            const round = f.ftype === "radio";
            const on = round ? f.exportValue || "Choice" : f.exportValue || "Yes";
            const onName = serialize(new PName(on));
            const yes = api.newRef(), off = api.newRef();
            const dot = round
                ? `0.1 0.2 0.5 rg ${ellipse(w / 2, h / 2, Math.min(w, h) * 0.22, Math.min(w, h) * 0.22)} f\n`
                : `0.1 0.2 0.5 RG ${num(Math.min(w, h) * 0.14)} w 1 J 1 j ${num(w * 0.22)} ${num(h * 0.5)} m ${num(w * 0.42)} ${num(h * 0.25)} l ${num(w * 0.8)} ${num(h * 0.75)} l S\n`;
            await form(off, w, h, box(w, h, round, f.style));
            await form(yes, w, h, box(w, h, round, f.style) + dot);
            const checked = f.defaultValue === "on" || f.defaultValue === on;
            const ap = `/AP << /N << ${onName} ${yes} 0 R /Off ${off} 0 R >> >>`;
            if (round) {
                const g = groups.get(f.name) ?? { parent: api.newRef(), kids: [], v: "Off", required: false };
                g.kids.push(n);
                if (checked) g.v = on;
                if (f.required) g.required = true;
                groups.set(f.name, g);
                api.writeObj(n, `<< ${common}/Parent ${g.parent} 0 R /AS ${checked ? onName : "/Off"} ${ap} >>`);
            } else {
                api.writeObj(n, `<< ${common}/FT /Btn /T ${lit(pdfText(f.name))} /Ff ${flags} /V ${checked ? onName : "/Off"} /AS ${checked ? onName : "/Off"} ${extra}${ap} >>`);
                top.push(new PRef(n, 0));
            }
            widgets.push(new PRef(n, 0));
            continue;
        }
        const ap = api.newRef();
        const comb = isComb(f);
        await form(ap, w, h, box(w, h, false, f.style, { comb: comb ? f.maxLen : 0, required: f.required }));
        if (f.ftype === "signature") {
            api.writeObj(n, `<< ${common}/FT /Sig /T ${lit(pdfText(f.name))} /Ff ${flags} /AP << /N ${ap} 0 R >> >>`);
        } else if (f.ftype === "combo" || f.ftype === "list") {
            const opt = f.options.map((o) => lit(pdfText(o))).join(" ");
            api.writeObj(n, `<< ${common}/FT /Ch /T ${lit(pdfText(f.name))} /Ff ${flags | (f.ftype === "combo" ? 131072 : 0) | (f.ftype === "list" && f.multiSelect ? FF_MULTISELECT : 0)} /Opt [${opt}] /DA (/Helv ${num(f.fontSize ?? 0)} Tf 0 g) ${f.defaultValue ? `/V ${lit(pdfText(f.defaultValue))} ` : ""}${extra}/AP << /N ${ap} 0 R >> >>`);
        } else {
            api.writeObj(n, `<< ${common}/FT /Tx /T ${lit(pdfText(f.name))} /Ff ${flags | (f.multiline ? 4096 : 0) | (f.password ? FF_PASSWORD : 0) | (comb ? FF_COMB : 0)} /DA (/Helv ${num(f.fontSize ?? 0)} Tf 0 g) ${f.maxLen ? `/MaxLen ${f.maxLen} ` : ""}${f.defaultValue ? `/V ${lit(pdfText(f.defaultValue))} ` : ""}${extra}/AP << /N ${ap} 0 R >> >>`);
        }
        widgets.push(new PRef(n, 0));
        top.push(new PRef(n, 0));
    }
    for (const [name, g] of groups) {
        api.writeObj(g.parent, `<< /FT /Btn /Ff ${49152 | (g.required ? 2 : 0)} /T ${lit(pdfText(name))} /Kids [${g.kids.map((k) => `${k} 0 R`).join(" ")}] /V ${serialize(new PName(g.v))} >>`);
        top.push(new PRef(g.parent, 0));
    }
    return { widgets, top };
}
